/* eslint-disable react-refresh/only-export-components -- The hook and its provider intentionally share a module. */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

// Estado del panel de ayuda (GOL-11). Vive en un contexto del shell porque lo
// abren tres superficies distintas —el botón «?» del header, el menú de cuenta y
// el pie del Sidebar— y todas comparten el mismo panel.
export type HelpView =
  | { kind: 'index' }
  | { kind: 'article'; id: string }
  | { kind: 'assistant'; question?: string }
  | { kind: 'contact' };

type HelpContextValue = {
  open: boolean;
  view: HelpView;
  openHelp: (view?: HelpView) => void;
  closeHelp: () => void;
  setView: (view: HelpView) => void;
};

const HelpContext = createContext<HelpContextValue | null>(null);

export function HelpProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<HelpView>({ kind: 'index' });

  const openHelp = useCallback((next?: HelpView) => {
    setView(next ?? { kind: 'index' });
    setOpen(true);
  }, []);

  const closeHelp = useCallback(() => setOpen(false), []);

  const value = useMemo(
    () => ({ open, view, openHelp, closeHelp, setView }),
    [open, view, openHelp, closeHelp],
  );

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelp(): HelpContextValue {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp debe usarse dentro de HelpProvider');
  return ctx;
}
